import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Drawer,
  Link,
  Typography,
  IconButton,
  ButtonBase,
  CircularProgress,
  Divider,
  Menu,
  MenuList,
  MenuItem,
  Collapse,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LogoutIcon from '@mui/icons-material/Logout';
import Avatar from '@mui/material/Avatar';
import { VotemateLogo } from '../votemate-logo/VotemateLogo';
import { useNavigate, useLocation } from 'react-router-dom';
import { EXTERNAL_URLS, INTERNAL_ROUTES } from '../../constants/urls';
import { useAuth } from '../auth/AuthProvider';
import { useBallotSheet } from '@/providers/BallotSheetProvider';
import { useNavMode } from '@/hooks/useNavMode';
import useAnalytics from '../../hooks/useAnalytics';
import { useAccountMenuItems } from './useAccountMenuItems';
import ReferVoteMateDialog from './ReferVoteMateDialog';
import { primitiveColors, semanticColors, semanticGradients, BORDER_RADIUS } from '@/theme/tokens';
import useApi from '../../hooks/useApi';
import { useDirection } from '../../hooks/useDirection';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BallotStatusDot from '@/components/ballot/BallotStatusDot';
import { useBallotRecords } from '@/contexts/BallotRecordsContext';
import { BallotRecordSummary, getBallotDotState, hasUpcomingElection } from '@/types/ballotRecords';
import { extraElectionCount, formatBallotRecordName } from '@/utils/ballotRecordName';

// Max ballot-history rows before the list scrolls internally (no show-all toggle).
const VISIBLE_BALLOT_RECORDS = 5;

// Ties the account-menu trigger to the accordion panel it expands via aria-controls.
const ACCOUNT_MENU_ID = 'left-pane-account-menu';

// Drawer surface, and the slightly deeper tint used for hover fills on it.
const DRAWER_BG = 'rgb(205, 216, 227)';
const DRAWER_BG_HOVER = 'rgb(195, 208, 221)';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChatSummary {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

// Single left-padding value applied to EVERY row — eliminates mixed indentation.
// Every row indents the same amount at every breakpoint, the account row included.
const PL = '15px';

// Neutral outline around the account row on desktop, marking the pill that wraps
// the avatar. Gradient borders stay reserved for AI features.
const accountRowBorderSx = {
  border: { xs: 'none', md: `1px solid ${semanticColors.borderMedium}` },
} as const;

// ─── Component ────────────────────────────────────────────────────────────────

export interface LeftPaneMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children?: React.ReactNode;
  guestMode?: boolean;
  onOpenBallot?: () => void;
  // Override the feedback action. Defaults to opening the global survey panel;
  // pages embedding the feedback form (e.g. Help) pass a handler that scrolls to
  // their section instead, so the panel doesn't defer-open elsewhere.
  onFeedbackClick?: () => void;
  // Trigger opening the account popover from outside (e.g. when the icon in the strip is clicked)
  shouldOpenAccountMenu?: boolean;
  onAccountMenuOpened?: () => void;
}

const LeftPaneMenu: React.FC<LeftPaneMenuProps> = ({
  isOpen,
  onClose,
  onMouseEnter,
  onMouseLeave,
  children,
  guestMode,
  onOpenBallot,
  onFeedbackClick,
  shouldOpenAccountMenu,
  onAccountMenuOpened,
}) => {
  const { t } = useTranslation('common');
  const { t: tBallot, i18n } = useTranslation('ballot');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, signIn, signOut } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const api = useApi();
  const { track } = useAnalytics();
  const ballotRecords = useBallotRecords();
  const dir = useDirection();

  // Ballot-history section state
  const [hoveredBallotId, setHoveredBallotId] = useState<string | null>(null);
  const [ballotToDelete, setBallotToDelete] = useState<BallotRecordSummary | null>(null);

  // Conversation list state
  const [conversations, setConversations] = useState<ChatSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const hasFetchedRef = useRef(false);

  // Three-dot context menu
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuConvId, setMenuConvId] = useState<string | null>(null);
  const [hoveredConvId, setHoveredConvId] = useState<string | null>(null);

  // Account menu — holds the account-level actions that used to sit as their own
  // rail rows (support, refer, feedback, help, settings) plus sign out. It expands
  // upward from the trigger row as an accordion panel that overlays the drawer's
  // ballot/chat lists rather than floating outside the drawer.
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  // Guests have no account, so the same menu hangs off a "More options" row and
  // omits the account header and sign out.
  const isSignedIn = Boolean(isAuthenticated && user && !guestMode);

  // Nickname for the account row: display name, or the local part of either the
  // name-as-email or the email itself.
  const accountDisplayName =
    (user?.name?.includes('@') ? user?.name?.split('@')[0] : user?.name) ||
    user?.email?.split('@')[0];

  // The rail's account avatar opens the drawer and expands the accordion in one
  // step, so it reports the same event as the trigger row with its own source.
  useEffect(() => {
    if (shouldOpenAccountMenu) {
      setAccountMenuOpen(true);
      track('Account Menu Opened', { source: 'rail_avatar', guest: !isSignedIn });
      onAccountMenuOpened?.();
    }
  }, [shouldOpenAccountMenu, onAccountMenuOpened, track, isSignedIn]);

  // Collapse the panel whenever the drawer itself closes so it never reopens
  // expanded from a previous session.
  useEffect(() => {
    if (!isOpen) setAccountMenuOpen(false);
  }, [isOpen]);

  const handleMenuTriggerClick = () => {
    const next = !accountMenuOpen;
    setAccountMenuOpen(next);
    if (next) track('Account Menu Opened', { source: 'left_menu', guest: !isSignedIn });
  };

  const handleAccountMenuClose = () => {
    setAccountMenuOpen(false);
  };

  // Escape collapses the panel and returns focus to the trigger. Stopping
  // propagation keeps the drawer itself open on that first Escape.
  const handleAccountMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setAccountMenuOpen(false);
      menuTriggerRef.current?.focus();
    }
  };

  const handleSignOutMenuClick = () => {
    handleAccountMenuClose();
    signOut();
  };

  // Referral share dialog — rendered outside the Drawer so it clears the overlay.
  const [shareOpen, setShareOpen] = useState(false);

  // Support, refer, feedback, help, settings — shared with the mobile header's
  // avatar menu. Each action collapses the accordion and closes the drawer first.
  const accountMenuItems = useAccountMenuItems({
    source: 'account_menu',
    onDismiss: () => {
      handleAccountMenuClose();
      onClose();
    },
    onRefer: () => setShareOpen(true),
    onFeedbackClick,
  });

  // Mobile uses the BallotSheet for the ballot UX (bottom sheet that
  // overlays chat). Opening a ballot history row snaps it to ``mid`` when
  // closed. We deliberately skip the legacy
  // ``MobileLayoutContext.toggleMobileLayout`` / ``ballotOpen`` path on
  // mobile because flipping that state also flips the main panel's flex
  // sizing in DashboardLayout, which squishes the top app bar.
  const {
    detent: ballotSheetDetent,
    snapTo: snapBallotSheet,
    setActiveTab: setMobileTab,
  } = useBallotSheet();
  const navMode = useNavMode();

  // ── Fetch conversation history ──────────────────────────────────────────────

  const fetchHistory = useCallback(async () => {
    // Only show spinner when there's nothing cached to display
    setLoading((prev) => (conversations.length === 0 ? true : prev));
    setFetchError(false);
    try {
      const response = await api('/chat/history');
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setConversations(data.conversations || []);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [api, conversations.length]);

  // Stale-while-revalidate: show cached conversations instantly on drawer
  // open, then refresh in the background. No spinner unless the cache is empty.
  const activeConvIdForCache = location.pathname.startsWith('/chat/')
    ? location.pathname.replace('/chat/', '').split('/')[0]
    : null;
  const prevActiveConvRef = useRef(activeConvIdForCache);

  useEffect(() => {
    // When the active conversation changes, mark data as stale so next
    // drawer open triggers a background refresh.
    if (activeConvIdForCache !== prevActiveConvRef.current) {
      prevActiveConvRef.current = activeConvIdForCache;
      hasFetchedRef.current = false;
    }
  }, [activeConvIdForCache]);

  useEffect(() => {
    if (!isOpen || !isAuthenticated || !user) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    // Only show spinner if there's nothing cached yet
    if (conversations.length === 0) {
      fetchHistory();
    } else {
      // Silently refresh in the background — cached list stays visible
      fetchHistory();
    }
  }, [isOpen, isAuthenticated, user, fetchHistory, conversations.length]);

  // ── Derived state ───────────────────────────────────────────────────────────

  const sortedConversations = [...conversations].sort(
    (a, b) =>
      new Date(b.updated_at || b.created_at).getTime() -
      new Date(a.updated_at || a.created_at).getTime()
  );
  const activeConvId = location.pathname.startsWith('/chat/')
    ? location.pathname.replace('/chat/', '').split('/')[0]
    : null;

  // ── Handlers ───────────────────────────────────────────────────────────────

  // Open the ballot panel/sheet — used when a history row should always land
  // the user on the ballot it just selected.
  const openBallotPanel = () => {
    if (isMobile) {
      // Tab mode always switches to the Ballot tab; sheet mode opens the sheet
      // to mid only when it's currently closed.
      if (navMode === 'tab') {
        setMobileTab('ballot');
      } else if (ballotSheetDetent === 'closed') {
        snapBallotSheet('mid');
      } else {
        return;
      }
      const onChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat/');
      if (!onChatPage) {
        if (window.history.state && window.history.state.idx > 0) {
          navigate(-1);
        } else {
          navigate('/chat');
        }
      }
    } else if (onOpenBallot) {
      onOpenBallot();
    }
    onClose();
  };

  const handleBallotRecordClick = (record: BallotRecordSummary) => {
    track('Ballot Record Opened', { ballotId: record.id, source: 'left_menu' });
    // Selecting a ballot makes it the active selection: the backend syncs the
    // current address to it and re-points the chat context to its election.
    void ballotRecords.activateBallot(record.id);
    openBallotPanel();
  };

  // Only labeled entries get a menu row (past elections + the address's soonest-
  // upcoming). Later elections at the same address are reachable via the ballot
  // view's tabs, not as their own rows.
  const visibleBallotRecords = ballotRecords.records.filter((r) => r.is_visible_entry !== false);

  // The trash button also closes the drawer, unmounting the opener control, so
  // return focus to the menu button on dismiss for keyboard/screen-reader users.
  const closeDeleteDialog = () => {
    setBallotToDelete(null);
    requestAnimationFrame(() => {
      document.getElementById('main-menu-button')?.focus();
    });
  };

  const handleConfirmDeleteBallot = async () => {
    if (!ballotToDelete) return;
    const idToDelete = ballotToDelete.id;
    closeDeleteDialog();
    const ok = await ballotRecords.deleteRecord(idToDelete);
    if (ok) track('Ballot Record Deleted', { ballotId: idToDelete });
  };

  const handleNewChatClick = () => {
    track('New Chat Started', { source: 'left_menu' });
    navigate(INTERNAL_ROUTES.CHAT);
    onClose();
  };

  const handleConversationClick = (id: string) => {
    navigate(`/chat/${id}`);
    onClose();
  };

  const handleDotMenuOpen = (e: React.MouseEvent<HTMLElement>, id: string) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuConvId(id);
  };

  const handleDotMenuClose = () => {
    setMenuAnchor(null);
    setMenuConvId(null);
    setHoveredConvId(null);
  };

  const handleDeleteConversation = async () => {
    if (!menuConvId) return;
    const idToDelete = menuConvId;
    const isActiveConv = idToDelete === activeConvId;
    handleDotMenuClose();
    try {
      const response = await api(`/chat/conversation/${idToDelete}`, { method: 'DELETE' });
      if (response.ok) {
        setConversations((prev) => prev.filter((c) => c.id !== idToDelete));
        if (isActiveConv) {
          navigate(INTERNAL_ROUTES.CHAT);
          onClose();
        }
      }
    } catch {
      // silently fail — user can retry via the history page
    }
  };

  const handleRetry = () => {
    hasFetchedRef.current = true;
    fetchHistory();
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const renderConversationItem = (conv: ChatSummary) => {
    const isActive = conv.id === activeConvId;
    return (
      <Box
        role="listitem"
        key={conv.id}
        onMouseEnter={() => setHoveredConvId(conv.id)}
        onMouseLeave={() => setHoveredConvId(null)}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <ButtonBase
          onClick={() => handleConversationClick(conv.id)}
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingInlineStart: PL,
            paddingInlineEnd: '40px',
            py: '7px',
            textAlign: 'start',
            borderRadius: BORDER_RADIUS,
            backgroundColor: isActive ? semanticColors.overlaySelected : 'transparent',
            '&:hover': {
              backgroundColor: isActive
                ? semanticColors.overlaySelected
                : semanticColors.overlaySubtle,
            },
          }}
        >
          <Typography
            noWrap
            sx={{
              fontSize: '14px',
              color: semanticColors.textBody,
              fontWeight: isActive ? 600 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {conv.title || t('untitledConversation')}
          </Typography>
        </ButtonBase>

        {/* Three-dot menu button — visible on hover/focus */}
        <IconButton
          size="small"
          onClick={(e) => handleDotMenuOpen(e, conv.id)}
          aria-label={t('moreOptions')}
          sx={{
            position: 'absolute',
            insetInlineEnd: '8px',
            opacity: hoveredConvId === conv.id ? 1 : 0,
            transition: 'opacity 0.15s',
            color: semanticColors.textTertiary,
            width: '24px',
            height: '24px',
            padding: 0,
            '&:focus-visible': { opacity: 1 },
          }}
        >
          <MoreHorizIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      </Box>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <Drawer
        anchor={isMobile ? 'top' : dir === 'rtl' ? 'right' : 'left'}
        open={isOpen}
        onClose={onClose}
        variant="temporary"
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true,
          // Lets the LeftPaneMenu's "My Ballot Preview" item focus
          // the MobileAddressBar's input from inside the drawer.
          // ``disableEnforceFocus`` lets the focus call land on the
          // outside input in the first place (default MUI behavior
          // would trap it and bounce back). ``disableRestoreFocus``
          // stops MUI from restoring focus to the trigger element
          // (the hamburger) when the drawer then closes — without
          // this, the input takes focus for a frame and then loses
          // it as the modal restores. The drawer's contents are menu
          // items + buttons (no inputs), so the focus trap isn't
          // doing real accessibility work anyway.
          disableEnforceFocus: true,
          disableRestoreFocus: true,
          BackdropProps: {
            sx:
              dir === 'rtl'
                ? { right: { xs: 0, md: '35px' }, left: 'auto' }
                : { left: { xs: 0, md: '35px' } },
          },
        }}
        sx={{
          // Raise above the 30px white strip (z-index 1600) but keep hamburger (1700) on top
          zIndex: 1650,
          ...(dir === 'rtl'
            ? { right: { xs: 0, md: '35px' }, left: 'auto' }
            : { left: { xs: 0, md: '35px' } }),
        }}
        PaperProps={{
          elevation: 0,
          onMouseEnter,
          onMouseLeave,
          sx: {
            width: { xs: '100vw', md: '245px' },
            // ``dvh`` on mobile so the drawer's height tracks the
            // currently-visible viewport (URL bar collapse, keyboard
            // open). ``vh`` resolves to the URL-bar-hidden viewport
            // and leaves the drawer extending past the visible area
            // when either browser chrome is showing.
            height: { xs: '90dvh', md: '100vh' },
            maxHeight: { xs: '90dvh', md: '100vh' },
            ...(dir === 'rtl'
              ? { right: { xs: 0, md: '35px' }, left: 'auto' }
              : { left: { xs: 0, md: '35px' } }),
            boxSizing: 'border-box',
            backgroundColor: DRAWER_BG,
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.3s ease-in-out',
            border: 'none',
            borderRadius: { xs: '0 0 20px 20px', md: 0 },
            // Desktop: let the account row's avatar + border overflow left
            // over the white strip. The scrollable list (below) clips its
            // own overflow, so this only frees the decorative bottom overlay.
            overflow: { xs: 'hidden', md: 'visible' },
            boxShadow: 'none',
          },
        }}
        role="navigation"
        aria-label={t('mainMenu')}
      >
        {/* ── Top bar: logo ────────────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            paddingInlineStart: PL,
            paddingInlineEnd: '12px',
            py: '12px',
            flexShrink: 0,
          }}
        >
          <VotemateLogo size={24} />
        </Box>

        <Divider sx={{ opacity: 0.3 }} />

        {/* ── Ballot history (last 5, expandable) ──────────────────────────── */}
        {isAuthenticated && visibleBallotRecords.length > 0 && (
          <Box sx={{ flexShrink: 0, pb: '2px' }}>
            <Typography
              component="h3"
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: semanticColors.textTertiary,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                paddingInlineStart: PL,
                paddingInlineEnd: '12px',
                py: '4px',
              }}
            >
              {tBallot('records.sectionTitle')}
            </Typography>
            <Box
              role="list"
              id="left-menu-ballot-list"
              aria-label={tBallot('records.sectionTitle')}
              // Fixed ~5-row scroll window like the chat list (no show-all
              // toggle). ~48px per two-line row.
              sx={{
                maxHeight: VISIBLE_BALLOT_RECORDS * 48,
                overflowY: 'auto',
                minHeight: 0,
              }}
            >
              {visibleBallotRecords.map((record) => {
                const name = formatBallotRecordName(
                  record,
                  i18n.language,
                  tBallot('records.unnamed')
                );
                const extra = extraElectionCount(record);
                // Backend marks the row to highlight (handles viewing a tab-only
                // future election); fall back to the direct active id for an
                // instant highlight before the list refresh lands.
                const isActive =
                  record.is_active_entry === true ||
                  ballotRecords.activeBallotId === record.id ||
                  (!ballotRecords.activeBallotId && record.is_current_address);
                return (
                  <Box
                    role="listitem"
                    key={record.id}
                    onMouseEnter={() => setHoveredBallotId(record.id)}
                    onMouseLeave={() => setHoveredBallotId(null)}
                    sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                  >
                    <ButtonBase
                      onClick={() => handleBallotRecordClick(record)}
                      aria-label={tBallot('records.openAriaLabel', { name })}
                      aria-pressed={isActive}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '8px',
                        paddingInlineStart: PL,
                        paddingInlineEnd: '40px',
                        py: '6px',
                        textAlign: 'start',
                        borderRadius: BORDER_RADIUS,
                        backgroundColor: isActive ? semanticColors.overlaySelected : 'transparent',
                        '&:hover': {
                          backgroundColor: isActive
                            ? semanticColors.overlaySelected
                            : semanticColors.overlaySubtle,
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${primitiveColors.logoPurple}`,
                          outlineOffset: '-2px',
                          zIndex: 1,
                        },
                      }}
                    >
                      <BallotStatusDot
                        state={getBallotDotState(
                          record.status,
                          hasUpcomingElection(record.elections),
                          record.is_complete
                        )}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: '13px',
                            color: semanticColors.textBody,
                            fontWeight: isActive ? 600 : 400,
                          }}
                        >
                          {name}
                          {extra > 0 && ` ${tBallot('records.moreElections', { count: extra })}`}
                        </Typography>
                        {record.location_label && (
                          <Typography
                            noWrap
                            sx={{
                              fontSize: '11px',
                              // Darker on the selected-row overlay to hold AA contrast.
                              color: isActive
                                ? semanticColors.textBody
                                : semanticColors.textTertiary,
                            }}
                          >
                            {record.location_label}
                          </Typography>
                        )}
                      </Box>
                    </ButtonBase>

                    {/* Trash — revealed on hover/focus like the chat kebab; the
                        mobile layout keeps it visible since touch has no hover
                        (matching the mobile app). */}
                    <IconButton
                      size="small"
                      onClick={() => {
                        // Close the drawer so the confirm dialog (rendered
                        // outside it) isn't hidden behind the sidebar.
                        setBallotToDelete(record);
                        onClose();
                      }}
                      aria-label={tBallot('records.deleteAriaLabel', { name })}
                      sx={{
                        position: 'absolute',
                        insetInlineEnd: '8px',
                        opacity: isMobile || hoveredBallotId === record.id ? 1 : 0,
                        transition: 'opacity 0.15s',
                        color: semanticColors.textTertiary,
                        width: '24px',
                        height: '24px',
                        padding: 0,
                        '&:focus-visible': { opacity: 1 },
                        '&:hover': { color: semanticColors.danger },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ── Chats header: section label + New Chat chip, right-aligned ───── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingInlineStart: PL,
            paddingInlineEnd: '8px',
            py: '4px',
            flexShrink: 0,
          }}
        >
          <Typography
            component="h3"
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: semanticColors.textTertiary,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}
          >
            {t('chats')}
          </Typography>
          {/* AI-action treatment: a gradient keyline with no fill. The inner
              layer paints the drawer's own color so the chip reads as an
              outline sitting on the drawer. */}
          <ButtonBase
            onClick={handleNewChatClick}
            disableRipple
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              height: 28,
              pl: '8px',
              pr: '10px',
              borderRadius: BORDER_RADIUS,
              border: '1.5px solid transparent',
              background: `linear-gradient(${DRAWER_BG}, ${DRAWER_BG}) padding-box, ${semanticGradients.primary} border-box`,
              // A bare <button> takes the UA font; inherit DM Sans from the page.
              fontFamily: 'inherit',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.01em',
              color: semanticColors.textBody,
              whiteSpace: 'nowrap',
              '&:hover': {
                background: `linear-gradient(${DRAWER_BG_HOVER}, ${DRAWER_BG_HOVER}) padding-box, ${semanticGradients.primary} border-box`,
              },
              '&:focus-visible': {
                outline: `2px solid ${primitiveColors.logoPurple}`,
                outlineOffset: '2px',
              },
            }}
          >
            <AddIcon sx={{ fontSize: 15 }} aria-hidden />
            {t('newChat')}
          </ButtonBase>
        </Box>

        <Divider sx={{ opacity: 0.3, mt: '4px' }} />

        {/* ── Conversation list ────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflowY: 'auto', py: '4px', minHeight: 0 }}>
          {!isAuthenticated ? (
            /* Unauthenticated: conversion-focused sign-in / sign-up card,
               vertically centered in the otherwise-empty history area. */
            <Box
              sx={{
                paddingInline: PL,
                py: '12px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box
                role="region"
                aria-labelledby="menu-signin-cta-title"
                sx={{
                  borderRadius: BORDER_RADIUS,
                  backgroundColor: semanticColors.surface,
                  p: '14px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
                }}
              >
                <Typography
                  id="menu-signin-cta-title"
                  component="h2"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: semanticColors.textBody,
                    mb: '4px',
                  }}
                >
                  {t('signInPromptTitle')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: semanticColors.textTertiary,
                    lineHeight: 1.45,
                    mb: '12px',
                  }}
                >
                  {t('signInPromptBody')}
                </Typography>
                <Button
                  fullWidth
                  disableRipple
                  onClick={() => {
                    track('Menu Sign In Clicked');
                    signIn();
                    onClose();
                  }}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                    py: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    letterSpacing: '0.01em',
                    backgroundColor: semanticColors.textPrimary,
                    color: semanticColors.textOnPrimary,
                    boxShadow: 'none',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: semanticColors.brandAccent,
                      boxShadow: 'none',
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${semanticColors.brandPrimary}`,
                      outlineOffset: '2px',
                    },
                  }}
                >
                  {t('signInSignUp')}
                </Button>
              </Box>
            </Box>
          ) : loading ? (
            /* Loading state */
            <Box sx={{ display: 'flex', justifyContent: 'center', py: '24px' }}>
              <CircularProgress size={20} sx={{ color: semanticColors.textTertiary }} />
            </Box>
          ) : fetchError ? (
            /* Error state */
            <Box sx={{ paddingInlineStart: PL, paddingInlineEnd: '12px', py: '12px' }}>
              <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
                {t('couldntLoadHistory')} ·{' '}
                <ButtonBase
                  onClick={handleRetry}
                  sx={{
                    fontSize: '13px',
                    color: primitiveColors.blue3,
                    textDecoration: 'underline',
                    verticalAlign: 'baseline',
                  }}
                >
                  {t('retry')}
                </ButtonBase>
              </Typography>
            </Box>
          ) : conversations.length === 0 ? (
            /* Empty state */
            <Box sx={{ paddingInlineStart: PL, paddingInlineEnd: '12px', py: '12px' }}>
              <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary }}>
                {t('noConversationsYet')}
              </Typography>
            </Box>
          ) : (
            /* Flat conversation list — most recent first */
            <Box role="list">{sortedConversations.map(renderConversationItem)}</Box>
          )}
        </Box>

        {/* ── Three-dot context menu ────────────────────────────────────────── */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleDotMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: 1660 }}
        >
          <MenuItem
            onClick={handleDeleteConversation}
            sx={{ color: semanticColors.danger, fontSize: '14px' }}
          >
            {t('delete')}
          </MenuItem>
        </Menu>

        <Divider sx={{ opacity: 0.3 }} />

        {/* ── Bottom: account / more trigger + footer ─────────────────────────── */}
        <Box sx={{ flexShrink: 0, pb: '8px' }}>
          {/* ``ClickAwayListener`` wraps the panel and its trigger, so clicking the
              trigger toggles rather than closing-then-reopening the panel. The footer
              links below sit outside it on purpose: they read as outside the panel, so
              pressing one collapses it. It listens on release rather than click so the
              rail's account glyph — which expands the panel on click — opens it after
              the away-press has been handled, while still letting a pointer/touch drag
              off the trigger cancel the interaction (WCAG 2.5.2 Pointer Cancellation). */}
          <ClickAwayListener
            onClickAway={handleAccountMenuClose}
            mouseEvent="onMouseUp"
            touchEvent="onTouchEnd"
          >
            <Box sx={{ position: 'relative' }}>
              {/* Accordion panel: grows upward out of the trigger row and overlays
                the ballot/chat lists instead of floating outside the drawer. It
                stays within the drawer, flush with the account row below it. */}
              <Collapse
                in={accountMenuOpen}
                timeout={200}
                unmountOnExit
                sx={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 2,
                }}
              >
                <Box
                  id={ACCOUNT_MENU_ID}
                  onKeyDown={handleAccountMenuKeyDown}
                  sx={{
                    backgroundColor: semanticColors.surface,
                    borderRadius: `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`,
                    boxShadow: `0 -6px 18px ${semanticColors.shadowLight}`,
                    maxHeight: { xs: '65dvh', md: '70vh' },
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                  }}
                >
                  {isSignedIn && (
                    <>
                      <Box sx={{ px: '16px', py: '12px' }}>
                        {accountDisplayName && (
                          <Typography
                            noWrap
                            sx={{
                              fontWeight: 600,
                              fontSize: '14px',
                              color: semanticColors.textBody,
                            }}
                          >
                            {accountDisplayName}
                          </Typography>
                        )}
                        {user?.email && (
                          <Typography
                            noWrap
                            sx={{ fontSize: '12px', color: semanticColors.textTertiary }}
                          >
                            {user.email}
                          </Typography>
                        )}
                      </Box>
                      <Divider sx={{ opacity: 0.3 }} />
                    </>
                  )}
                  <MenuList
                    autoFocusItem={accountMenuOpen}
                    aria-label={isSignedIn ? t('accountMenu') : t('moreOptions')}
                    sx={{ py: '4px' }}
                  >
                    {accountMenuItems.map(({ key, label, icon, onClick }) => (
                      <MenuItem key={key} onClick={onClick} sx={{ gap: '10px', px: 2, py: 1.25 }}>
                        {icon}
                        <Typography sx={{ fontSize: '14px', color: semanticColors.textBody }}>
                          {label}
                        </Typography>
                      </MenuItem>
                    ))}
                    {isSignedIn && <Divider sx={{ opacity: 0.3 }} />}
                    {isSignedIn && (
                      <MenuItem
                        onClick={handleSignOutMenuClick}
                        sx={{ gap: '10px', px: 2, py: 1.25 }}
                      >
                        <LogoutIcon sx={{ fontSize: '18px', color: semanticColors.textTertiary }} />
                        <Typography sx={{ fontSize: '14px', color: semanticColors.textBody }}>
                          {t('signOut')}
                        </Typography>
                      </MenuItem>
                    )}
                  </MenuList>
                </Box>
              </Collapse>

              {/* One row stands in for the six that used to live here. Signed-in users
              get their account row; guests get a "More options" row onto the same
              menu, minus the account header and sign out. */}
              <ButtonBase
                ref={menuTriggerRef}
                onClick={handleMenuTriggerClick}
                aria-haspopup="menu"
                aria-expanded={accountMenuOpen}
                aria-controls={accountMenuOpen ? ACCOUNT_MENU_ID : undefined}
                {...(isSignedIn ? {} : { 'aria-label': t('moreOptions') })}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '10px',
                  paddingInlineStart: PL,
                  width: '100%',
                  ...(isSignedIn ? accountRowBorderSx : {}),
                  paddingInlineEnd: '12px',
                  minHeight: '48px',
                  py: '6px',
                  textAlign: 'start',
                  borderRadius: BORDER_RADIUS,
                  '&:hover': { backgroundColor: semanticColors.overlaySubtle },
                  '&:focus-visible': {
                    outline: `2px solid ${semanticColors.brandPrimary}`,
                    outlineOffset: '2px',
                  },
                  transition: 'background-color 0.15s ease',
                }}
              >
                {isSignedIn ? (
                  <Avatar
                    aria-hidden
                    src={user?.picture || undefined}
                    alt={user?.name || t('userAvatarAlt')}
                    sx={{ width: 21, height: 21, fontSize: '10px', flexShrink: 0 }}
                  >
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                ) : (
                  <MoreHorizIcon
                    sx={{ fontSize: '20px', color: semanticColors.textBody, flexShrink: 0 }}
                  />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: '14px',
                      color: semanticColors.textBody,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {isSignedIn ? accountDisplayName : t('moreOptions')}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: '11px',
                      color: semanticColors.textTertiary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {isSignedIn ? t('accountRowSubtitle') : t('guestMenuRowSubtitle')}
                  </Typography>
                </Box>
                <KeyboardArrowUpIcon
                  sx={{
                    fontSize: '18px',
                    color: semanticColors.textTertiary,
                    flexShrink: 0,
                    transform: accountMenuOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s ease',
                  }}
                />
              </ButtonBase>
            </Box>
          </ClickAwayListener>

          {/* Footer links: Privacy · Terms · About VoteMate */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px 12px',
              paddingInlineStart: PL,
              paddingInlineEnd: '12px',
              pt: '6px',
              pb: '4px',
            }}
          >
            {[
              { labelKey: 'privacy', href: EXTERNAL_URLS.PRIVACY_POLICY },
              { labelKey: 'terms', href: EXTERNAL_URLS.TERMS_OF_USE },
              { labelKey: 'aboutVoteMate', href: EXTERNAL_URLS.VOTEMATE_ORG },
            ].map(({ labelKey, href }) => (
              <Link
                key={labelKey}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: '11px',
                  color: semanticColors.textTertiary,
                  textDecoration: 'underline',
                  '&:hover': { color: semanticColors.textBody },
                }}
              >
                {t(labelKey)}
              </Link>
            ))}
          </Box>
        </Box>

        {children}
      </Drawer>

      {/* ── Delete-ballot confirm dialog — rendered outside the Drawer so it clears the
           sidebar overlay/focus; the trash button also closes the menu. ── */}
      <Dialog
        open={Boolean(ballotToDelete)}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-ballot-title"
        aria-describedby="delete-ballot-body"
      >
        <DialogTitle id="delete-ballot-title" sx={{ fontSize: '16px', fontWeight: 700 }}>
          {tBallot('records.deleteTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography
            id="delete-ballot-body"
            sx={{ fontSize: '13px', color: semanticColors.textTertiary, mb: '16px' }}
          >
            {tBallot('records.deleteBody')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              onClick={closeDeleteDialog}
              sx={{ textTransform: 'none', color: semanticColors.textBody }}
            >
              {tBallot('records.cancel')}
            </Button>
            <Button
              onClick={() => void handleConfirmDeleteBallot()}
              sx={{ textTransform: 'none', color: semanticColors.danger, fontWeight: 600 }}
            >
              {tBallot('records.delete')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <ReferVoteMateDialog open={shareOpen} onClose={() => setShareOpen(false)} />
    </>
  );
};

export default LeftPaneMenu;
